import React, { useState } from 'react';
import { Calendar, Clock, Trophy, Target, BookOpen, MessageSquare, Lightbulb, UserCheck } from 'lucide-react';

// Student Day-in-the-Life Component
const StudentDayComponent = () => {
  // Change the state type definition from null to number | null
const [activeQuestion, setActiveQuestion] = useState<number | null>(null);
  return (
    <div className="container mx-auto px-4 md:px-6 py-16 bg-gray-50">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">A Day in the Life of an NAE Student</h2>
      <p className="text-lg text-gray-700 text-center mb-12 max-w-3xl mx-auto">
        Experience what your daily learning journey looks like with NAE Coaching
      </p>
      
      <div className="relative">
        {/* Timeline line */}
        <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-indigo-200"></div>
        
        {/* Timeline items */}
        <div className="space-y-12">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 md:pr-16 md:text-right mb-6 md:mb-0">
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                <h3 className="text-xl font-bold text-indigo-700 mb-2">8:00 AM - Morning Prep</h3>
                <p className="text-gray-600">
                  Start your day by reviewing recorded concept videos from the NAE App before joining your live digital class.
                </p>
              </div>
            </div>
            <div className="md:w-12 flex justify-center">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center shadow-lg z-10">
                <Clock className="text-white h-6 w-6" />
              </div>
            </div>
            <div className="md:w-1/2 md:pl-16 hidden md:block"></div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 md:pr-16 hidden md:block"></div>
            <div className="md:w-12 flex justify-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center shadow-lg z-10">
                <BookOpen className="text-white h-6 w-6" />
              </div>
            </div>
            <div className="md:w-1/2 md:pl-16 md:text-left mb-6 md:mb-0">
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                <h3 className="text-xl font-bold text-purple-700 mb-2">10:00 AM - Interactive Live Class</h3>
                <p className="text-gray-600">
                  Engage in our digital board class where concepts come alive through interactive visualizations and real-time problem solving.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 md:pr-16 md:text-right mb-6 md:mb-0">
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                <h3 className="text-xl font-bold text-blue-700 mb-2">2:00 PM - Problem-Solving Session</h3>
                <p className="text-gray-600">
                  Tackle JEE/NEET level problems with guided support from our expert faculty who break down complex concepts.
                </p>
              </div>
            </div>
            <div className="md:w-12 flex justify-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg z-10">
                <Target className="text-white h-6 w-6" />
              </div>
            </div>
            <div className="md:w-1/2 md:pl-16 hidden md:block"></div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 md:pr-16 hidden md:block"></div>
            <div className="md:w-12 flex justify-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center shadow-lg z-10">
                <Trophy className="text-white h-6 w-6" />
              </div>
            </div>
            <div className="md:w-1/2 md:pl-16 md:text-left mb-6 md:mb-0">
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                <h3 className="text-xl font-bold text-green-700 mb-2">5:00 PM - Progress Assessment</h3>
                <p className="text-gray-600">
                  Test your understanding with daily quizzes and get personalized feedback on your strengths and areas to improve.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 md:pr-16 md:text-right mb-6 md:mb-0">
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                <h3 className="text-xl font-bold text-amber-700 mb-2">8:00 PM - Doubt Clearing</h3>
                <p className="text-gray-600">
                  Connect with teachers through our digital doubt-clearing sessions where no question is too small or too complex.
                </p>
              </div>
            </div>
            <div className="md:w-12 flex justify-center">
              <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center shadow-lg z-10">
                <Lightbulb className="text-white h-6 w-6" />
              </div>
            </div>
            <div className="md:w-1/2 md:pl-16 hidden md:block"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Student Concerns FAQ Component
const StudentFAQComponent = () => {
  // const [activeQuestion, setActiveQuestion] = useState(null);
  // Change the state type definition from null to number | null
  const [activeQuestion, setActiveQuestion] = useState<number | null>(null);
  
  const faqs = [
    {
      question: "I struggle with Science concepts. Will I be able to keep up?",
      answer: "Absolutely! Our approach breaks down complex concepts into easy-to-understand modules. With our recorded classes, you can revisit difficult topics as many times as needed until you master them. Our teachers also provide personalized attention to address your specific challenges."
    },
    {
      question: "How will NAE Coaching help me prepare for competitive exams?",
      answer: "Our curriculum is specifically designed to build both strong fundamentals and advanced problem-solving skills needed for JEE, NEET and other competitive exams. We include actual exam-pattern questions, regular mock tests, and specialized training for tackling the toughest problems you'll encounter."
    },
    {
      question: "I'm not great at self-study. Will I still benefit?",
      answer: "Yes! Our interactive teaching methods are designed to keep you engaged during class. We also provide structured study plans, regular assessments, and personal mentoring to help you develop effective study habits. Many students who previously struggled with self-study have thrived in our program."
    },
    {
      question: "What if I miss a live class?",
      answer: "No problem! All our classes are recorded and available on the NAE App. You can watch them anytime, pause, rewind, and learn at your own pace. Our teachers are also available for doubt-clearing sessions if you have questions after watching the recorded class."
    },
    {
      question: "Will NAE help with my board exams too, or just competitive exams?",
      answer: "We provide comprehensive preparation for both board exams and competitive entrance tests. Our teaching approach ensures you build a strong foundation that helps you excel in your school exams while also preparing you for future competitive challenges."
    }
  ];
  
  return (
    <div className="container mx-auto px-4 md:px-6 py-16">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">Questions Students Like You Ask</h2>
      <p className="text-lg text-gray-700 text-center mb-12 max-w-3xl mx-auto">
        We understand your concerns and we're here to address them
      </p>
      
      <div className="max-w-3xl mx-auto">
        {faqs.map((faq, index) => (
          <div key={index} className="mb-4">
            <button
              className={`w-full text-left p-4 rounded-lg flex justify-between items-center ${
                activeQuestion === index 
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md' 
                  : 'bg-white shadow hover:shadow-md'
              }`}
              onClick={() => setActiveQuestion(activeQuestion === index ? null : index)}
            >
              <span className="font-semibold">{faq.question}</span>
              <span className="transform transition-transform">
                {activeQuestion === index ? '−' : '+'}
              </span>
            </button>
            
            {activeQuestion === index && (
              <div className="bg-white p-4 rounded-b-lg shadow-md border-t border-gray-100 animate-fadeIn">
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Student Community Component
const StudentCommunityComponent = () => {
  return (
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 py-16">
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">Join Our Student Community</h2>
        <p className="text-lg text-gray-700 text-center mb-12 max-w-3xl mx-auto">
          Learning is better together. Connect with peers who share your academic goals and challenges.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-1">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4 mx-auto">
              <UserCheck className="text-indigo-600 h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-center mb-3">Peer Study Groups</h3>
            <p className="text-gray-600 text-center">
              Connect with classmates for collaborative learning and problem-solving sessions
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-1">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4 mx-auto">
              <MessageSquare className="text-purple-600 h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-center mb-3">Discussion Forums</h3>
            <p className="text-gray-600 text-center">
              Share ideas, ask questions, and explore concepts with our active online community
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-1">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
              <Calendar className="text-blue-600 h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-center mb-3">Events & Workshops</h3>
            <p className="text-gray-600 text-center">
              Participate in regular academic competitions, workshops, and motivational sessions
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-1">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 mx-auto">
              <Trophy className="text-green-600 h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-center mb-3">Recognition Program</h3>
            <p className="text-gray-600 text-center">
              Get recognized for your achievements and inspire others in our student spotlight
            </p>
          </div>
        </div>
        
        <div className="mt-12 bg-white p-8 rounded-xl shadow-lg max-w-3xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-full md:w-1/3">
              <img src="/api/placeholder/300/300" alt="Student Community" className="rounded-lg w-full" />
            </div>
            <div className="w-full md:w-2/3">
              <h3 className="text-2xl font-bold mb-4">Student Testimonial</h3>
              <p className="text-gray-600 italic mb-4">
                "The best part about NAE Coaching isn't just the excellent teaching, but the amazing community of friends I've made. We solve problems together, motivate each other, and celebrate each other's successes."
              </p>
              <div className="flex items-center">
                <div className="mr-4 w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <span className="font-bold text-indigo-600">AK</span>
                </div>
                <div>
                  <p className="font-semibold">Aman Kumar</p>
                  <p className="text-sm text-gray-500">JEE Advanced Top 500 Ranker</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Study Resources Component
const StudyResourcesComponent = () => {
  return (
    <div className="container mx-auto px-4 md:px-6 py-16">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">Student Resources You'll Love</h2>
      <p className="text-lg text-gray-700 text-center mb-12 max-w-3xl mx-auto">
        Everything you need to succeed, right at your fingertips
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all">
          <div className="p-6">
            <h3 className="text-2xl font-bold text-indigo-800 mb-4">NAE Mobile App</h3>
            <ul className="space-y-3">
              <li className="flex items-center">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <span className="text-gray-700">Watch all recorded lectures anytime, anywhere</span>
              </li>
              <li className="flex items-center">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <span className="text-gray-700">Practice with thousands of topic-wise questions</span>
              </li>
              <li className="flex items-center">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <span className="text-gray-700">Track your progress with detailed analytics</span>
              </li>
              <li className="flex items-center">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <span className="text-gray-700">Get instant doubt resolution through chat</span>
              </li>
            </ul>
            <div className="mt-6 flex gap-4">
              <button className="bg-black text-white px-4 py-2 rounded-lg flex items-center gap-2">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.21 2.33-.91 3.57-.84 1.5.12 2.66.82 3.38 2.08-3.12 1.85-2.61 5.83.49 7.11-.82 1.67-1.84 3.31-2.52 3.82z"/>
                  <path d="M12.03 7.25c-.15-2.23 1.66-4.22 3.74-4.99.21 2.21-1.53 4.36-3.74 4.99z"/>
                </svg>
                App Store
              </button>
              <button className="bg-black text-white px-4 py-2 rounded-lg flex items-center gap-2">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.18 20.82c.55.96 1.59 1.18 2.56.84l.17-.07c.29-.11.53-.29.71-.55l.09-.17 1.73-3.02L12 20.01l3.56-2.16 1.73 3.02.09.17c.18.26.42.44.71.55l.17.07c.97.34 2.01.12 2.56-.84l1.1-1.91c.46-.83.24-1.87-.53-2.45L16.26 12l5.13-3.45c.77-.58.99-1.62.53-2.45l-1.1-1.91c-.55-.96-1.59-1.18-2.56-.84l-.17.07c-.29.11-.53.29-.71.55l-.09.17L15.56 7.2 12 5.04 8.44 7.2 6.71 4.18l-.09-.17c-.18-.26-.42-.44-.71-.55l-.17-.07c-.97-.34-2.01-.12-2.56.84l-1.1 1.91c-.46.83-.24 1.87.53 2.45L7.74 12l-5.13 3.45c-.77.58-.99 1.62-.53 2.45l1.1 1.91z" />
                </svg>
                Play Store
              </button>
            </div>
          </div>
          <img src="/api/placeholder/600/300" alt="NAE App Preview" className="w-full" />
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all">
          <div className="p-6">
            <h3 className="text-2xl font-bold text-purple-800 mb-4">Study Materials & Support</h3>
            <ul className="space-y-3">
              <li className="flex items-center">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <span className="text-gray-700">Comprehensive study notes curated by top educators</span>
              </li>
              <li className="flex items-center">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <span className="text-gray-700">Previous years' question papers with detailed solutions</span>
              </li>
              <li className="flex items-center">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <span className="text-gray-700">Formula sheets and quick revision guides</span>
              </li>
              <li className="flex items-center">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <span className="text-gray-700">Personal academic counseling and guidance</span>
              </li>
            </ul>
            <div className="mt-8">
              <button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all shadow-md flex items-center justify-center gap-2">
                Download Free Sample Materials
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                </svg>
              </button>
            </div>
          </div>
          <img src="/api/placeholder/600/300" alt="Study Materials Preview" className="w-full" />
        </div>
      </div>
    </div>
  );
};

// These components can be imported and used in the main About page
export { StudentDayComponent, StudentFAQComponent, StudentCommunityComponent, StudyResourcesComponent };

// Sample usage in main About page component
const AdditionalComponentsExample = () => {
  return (
    <div>
      <StudentDayComponent />
      <StudentFAQComponent />
      <StudentCommunityComponent />
      <StudyResourcesComponent />
    </div>
  );
};

export default AdditionalComponentsExample;